import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { Bell } from "react-feather"
import NotificationItem from "../ui/NotificationItem/NotificationItem"
import Tabs from "../ui/Tabs/Tabs"
import { useDownloadCenter } from "../../pages/Notifications/downloadCenter"
import {
  downloadsOf,
  relativeTime,
  requestedAtOf,
  requestedLabel,
  unreadCount
} from "../../pages/Notifications/notificationsData"
import redirectIcon from "../../assets/images/notifications/notif-redirect.svg"
import s from "./NotificationsBell.module.scss"

/**
 * The header bell and the panel it opens.
 *
 * Lives in `components/layout` beside `UserMenu` because, like it, this is the
 * one piece of app chrome that knows about routing — `Header` stays
 * presentational and takes it through a slot.
 *
 * Open state belongs to `downloadCenter` rather than to this component: an
 * Export pressed on a table two levels down has to be able to raise this
 * panel, which is the whole point of the flow. The unread mark is counted off
 * the same list the panel shows, so a dot can't be offering something that
 * isn't there.
 */
const NotificationsBell = () => {
  const navigate = useNavigate()
  const { items, isLoading, error, panelOpen, closePanel, togglePanel, openFile, retry } =
    useDownloadCenter()
  const [tab, setTab] = useState("all")
  const rootRef = useRef(null)
  const triggerRef = useRef(null)

  const unread = useMemo(() => unreadCount(items), [items])
  const downloads = useMemo(() => downloadsOf(items), [items])
  const visible = tab === "downloads" ? downloads : items

  // No counts here, unlike the page: the popover is a peek, and the design
  // draws its tabs as two plain labels.
  const tabs = [
    { key: "all", label: "All" },
    { key: "downloads", label: "Downloads" }
  ]

  /**
   * Dismiss on a click outside or on Escape.
   *
   * Both, not just the click: a panel that can only be closed by clicking away
   * traps anyone working from the keyboard. Focus goes back to the bell so the
   * next Tab continues from where it left off rather than from the top.
   */
  useEffect(() => {
    if (!panelOpen) return undefined

    const onPointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) closePanel()
    }
    const onKeyDown = (event) => {
      if (event.key !== "Escape") return
      closePanel()
      triggerRef.current?.focus()
    }

    document.addEventListener("mousedown", onPointerDown)
    document.addEventListener("keydown", onKeyDown)

    return () => {
      document.removeEventListener("mousedown", onPointerDown)
      document.removeEventListener("keydown", onKeyDown)
    }
  }, [panelOpen, closePanel])

  const viewAll = useCallback(() => {
    closePanel()
    navigate("/notifications")
  }, [closePanel, navigate])

  return (
    <div className={s.root} ref={rootRef}>
      <button
        type="button"
        ref={triggerRef}
        className={s.trigger}
        aria-label={unread ? `Notifications, ${unread} unread` : "Notifications"}
        aria-expanded={panelOpen}
        aria-haspopup="dialog"
        onClick={togglePanel}
      >
        <Bell size={19} />
        {unread > 0 && <span className={s.dot} aria-hidden="true" />}
      </button>

      {panelOpen && (
        <div className={s.panel} role="dialog" aria-label="Notifications">
          <div className={s.head}>
            <h2 className={s.heading}>Notifications</h2>
          </div>

          <Tabs
            items={tabs}
            activeKey={tab}
            onChange={setTab}
            ariaLabel="Notification filter"
            className={s.tabs}
          />

          {/* The panel is a peek at the feed, so the list scrolls inside it
              rather than the panel growing to the height of the feed. */}
          <div className={s.list}>
            {visible.map((item) => (
              <NotificationItem
                key={item.id}
                notification={item}
                timeLabel={relativeTime(item.minutesAgo)}
                requestLabel={item.request ? requestedLabel(requestedAtOf(item)) : undefined}
                onOpenFile={() => openFile(item)}
                onRetryFile={() => retry(item)}
              />
            ))}

            {/* Three different silences, and they mean different things: the
                queue hasn't been read yet, it couldn't be, or it is empty. */}
            {!visible.length && isLoading && <p className={s.empty}>Loading your downloads…</p>}
            {!visible.length && !isLoading && error && <p className={s.empty}>{error}</p>}
            {!visible.length && !isLoading && !error && (
              <p className={s.empty}>No downloads yet.</p>
            )}
          </div>

          <div className={s.foot}>
            <button type="button" className={s.viewAll} onClick={viewAll}>
              View all Notifications
              <img src={redirectIcon} alt="" width={16} height={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default NotificationsBell
